import { pdf } from '@react-pdf/renderer';
import { DesignProps } from 'api/DesignApi';
import { FileMessage, Message, MessageTypeEnum } from 'api/MessagesApi';
import MessagePdf from 'components/Pdf/MessagePdf';
import { MouseEvent, ReactNode } from 'react';
import { isMobileOrTabletBrowser } from 'utils/utils';

type AllProps = {
  message: Message;
  messageFile?: FileMessage;
  designProps?: DesignProps;
  text?: ReactNode | string;
  disabled?: boolean;
  onError?: () => void;
  render?: (props: { onClick: (e: MouseEvent<HTMLElement>) => void; disabled: boolean }) => ReactNode;
};

export default function DownloadLink({
  message,
  messageFile,
  designProps,
  text = 'download',
  disabled = false,
  onError,
  render
}: AllProps) {
  const handleDownload = async () => {
    if (!message) {
      onError?.();
      return;
    }

    try {
      const fileName = messageFile?.fileName || `${message.id}.pdf`;

      if (messageFile?.signedUrl) {
        downloadFile(fileName, messageFile?.signedUrl);
      } else if (message.type === MessageTypeEnum.Text) {
        const blob = await pdf(<MessagePdf message={message} designProps={designProps} />).toBlob();

        const url = URL.createObjectURL(blob);
        downloadFile(fileName, url);
        URL.revokeObjectURL(url);
      } else {
        onError?.();
      }
    } catch {
      onError?.();
    }
  };

  const downloadFile = (fileName: string, url: string) => {
    if (isMobileOrTabletBrowser() && /^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) {
      return;
    }
    void handleDownload();
  };

  if (render) {
    return <>{render({ onClick: handleClick, disabled })}</>;
  }

  const isTextString = typeof text === 'string';

  if (isTextString) {
    return (
      <button
        type="button"
        aria-disabled={disabled}
        disabled={disabled}
        onClick={handleClick}
        style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', textDecoration: 'underline' }}
      >
        {text}
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-disabled={disabled}
      disabled={disabled}
      onClick={handleClick}
      style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit' }}
    >
      {text}
    </button>
  );
}
